import React, { ChangeEvent, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'store/ducks';

import Form from '../../Form';
import Text from '../../Text';
import { Button } from '../../Button';
import { Column, Row } from '../../Layout/Grid';
import { OpenSlideSuccess } from '../../OpenSlideSuccess';
import FileDropZone from 'components/FileDropZone';
import { Visibility } from 'components/Visibility';
import Select from 'components/Select';
import { TableNewDocument } from './TableNewDocument';
import toast from "components/Toast/dispatch";

import { legalDocumentModelCompleteButton } from './TableNewDocument/model';
import { EnumUserType } from 'models/User';

import {
  FileTypesAccept,
  typeDocuments,
  EnumTypeDocument,
  TypeDocumentsSelectProps,
  DataFileStateProps,
  TypeDocumentProps,
  allTypeDocument
} from './interface';

import { useLegalDocumentContext } from 'contexts/LegalDocument';
import UploadService from 'services/upload.service';
import CustomerService from 'services/customer.service';
import UserService from 'services/user.service';

import { SlideStatusEnum } from 'utils/Slider';

import { InputNameDocument } from './styles';

export const NewLegalDocumentSlider: React.FC = () => {

  const LIMIT_LEGAL_DOCUMENT = 10;

  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();

  const slider = useSelector((state: RootState) => state.slider);
  const user = useSelector((state: RootState) => state.user);
  const customer = useSelector((state: RootState) => state.customer);

  const { setLoadingLegalDocument } = useLegalDocumentContext();

  const [typeDocument, setTypeDocument] = useState<TypeDocumentProps>({
    realName: "",
    valueName: "",
    legalDocumentType: 0
  });

  const [nameDocument, setNameDocument] = useState<string>("");

  const [dataFile, setDataFile] = useState<DataFileStateProps>({
    file: undefined
  });

  const [newDocuments, setNewDocuments] = useState<any>([]);
  const [protocolNumber, setProtocolNumber] = useState('');

  const [error, setError] = useState({
    isError: false,
    msg: ""
  });

  const _customerService = new CustomerService();
  const _uploadService = new UploadService();
  const _userServices = new UserService();

  useEffect(() => {
    const isEditDocumentType = typeof slider?.data?.typeDocument === 'number' && slider.data.typeDocument !== '';

    if (isEditDocumentType) {
      const typeDocumentSelected = allTypeDocument[slider?.data.typeDocument];
      const realNameDocument: TypeDocumentsSelectProps = returnTypeDocument(typeDocumentSelected);

      setTypeDocument(
        {
          ...typeDocument,
          valueName: realNameDocument.value,
          realName: realNameDocument.label,
          legalDocumentType: realNameDocument.legalDocumentType
        });
    }

  }, [slider]);

  useEffect(() => {
    if (typeDocument.valueName) {
      const valueTypeDocument: any = returnTypeDocument(typeDocument.valueName);

      if (valueTypeDocument?.value === EnumTypeDocument.OUTROS) {
        setNameDocument("");
      } else {
        setNameDocument(valueTypeDocument.realName);
      }
    }
  }, [typeDocument]);

  const returnTypeDocument = (value: string) => {
    const returnTypeDocument: any = typeDocuments.find((type: TypeDocumentsSelectProps) => {
      if (value === type.value) {
        return type;
      }
    });

    return returnTypeDocument;
  };

  const onSubmitDocument = async () => {
    setLoading(true);
    setError({ isError: false, msg: "" });

    const customerId = user?.userType === EnumUserType.CUSTOMER ? user.selectedCustomerId : customer?.id;

    const objFile = newDocuments.map((element: any) => ({
      file: element.file,
      legalDocumentType: element.legalDocumentType,
      nameDocument: element.nameDocument
    }));

    const objPost = {
      customerId: customerId ?? '',
      brandId: "",
      buId: "",
      userType: user?.userType ?? 'customer',
      files: objFile
    };

    try {
      const response = await _uploadService.uploadLegalDocument(objPost);

      if (response.status === 400) {
        setError({ isError: true, msg: response.data?.data || "" });
        return;
      }

      if (response.status === 200) {
        setProtocolNumber(response.data?.protocolNumber);
        handleAdditionalRequests(customerId);
      }
    } catch (error) {
      setError({ isError: true, msg: "Um erro inesperado ocorreu" });
    } finally {
      setLoading(false);
    }
  };

  const handleAdditionalRequests = async (customerId: string | undefined) => {
    try {
      setLoadingLegalDocument(true);

      if (user?.userType === EnumUserType.EMPLOYEE) {
        const result = await _customerService.findById(customerId || '');
        if (result.data) {
          dispatch({ type: 'SET_CUSTOMER', data: result.data });
        }
      } else {
        const result = await _userServices.currentUser();
        dispatch({ type: 'SET_USER_DATA', data: result.data });
        dispatch({ type: 'SET_USER_ONLY_CUSTOMER_ID', selectedCustomerId: customerId });
      }
    } catch (innerError) {
      setError({
        isError: true,
        msg: "Ocorreu um erro inesperado durante solicitações adicionais"
      });
    } finally {
      setLoadingLegalDocument(false);
    }
  };

  const handleValidateInputAndAddNewDocument = () => {
    setErrorMessage("");

    const existInputEmpty: boolean = validateInputEmpty();

    if (existInputEmpty) {
      setErrorMessage("Favor revisar o preenchimento do formulário");
      return;
    }

    handleAddDocument();
  };

  const handleAddDocument = () => {

    const index = newDocuments.findIndex((doc: any) =>
      doc.valueTypeDocument === typeDocument.valueName &&
      (typeDocument.valueName !== "outros" || doc.nameDocument.trim() === nameDocument.trim()));

    if (index !== -1) {
      setNewDocuments(newDocuments.map((doc: any, i: number) =>
        i === index ? { ...doc, file: dataFile } : doc
      ));
    } else {

      const isLimitMax = limitMaxLegalDocument();

      if (isLimitMax) return;

      setNewDocuments([
        ...newDocuments,
        {
          realNameTypeDocument: typeDocument.realName,
          valueTypeDocument: typeDocument.valueName,
          legalDocumentType: typeDocument.legalDocumentType,
          nameDocument: nameDocument,
          file: dataFile.file
        }
      ]);
    }

    clearForm();
  };

  const limitMaxLegalDocument = () => {
    if (newDocuments.length >= LIMIT_LEGAL_DOCUMENT) {
      toast.info('Atenção', `A inclusão de documentos é limitado em ${LIMIT_LEGAL_DOCUMENT} arquivos.`);
      clearForm();
      return true;
    }

    return false;
  };

  const validateInputEmpty = () => {
    if (typeDocument && nameDocument && dataFile.file)
      return false;

    return true;
  };

  const handleAddTypeDocument = (e: ChangeEvent<HTMLSelectElement>) => {
    const realNameDocument: TypeDocumentsSelectProps = returnTypeDocument(e.target.value);

    setTypeDocument(
      {
        ...typeDocument,
        valueName: e.target.value,
        realName: realNameDocument.label,
        legalDocumentType: realNameDocument.legalDocumentType
      });
  };

  const removeLegalDocument = (itemDocument: any) => {
    const updatedDocuments = newDocuments.filter((doc: any) => doc.nameDocument !== itemDocument.nameDocument);
    setNewDocuments(updatedDocuments);
  };

  const clearForm = () => {
    setTypeDocument({
      realName: "",
      valueName: "",
      legalDocumentType: 0
    });

    setNameDocument("");
    setDataFile({
      file: undefined
    });
  };

  const makeRequestOnEnterPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') e.preventDefault();
  };

  const SHOW_ALERT_ERROR = error.isError && (!loading && protocolNumber === '');
  const SHOW_MSG_SUCCESS = !error.isError && (protocolNumber !== '');
  const SHOW_FORM = !error.isError && (protocolNumber === '');

  return (
    <>
      <Visibility visible={SHOW_ALERT_ERROR}>
        <OpenSlideSuccess
          title='Ocorreu um erro ao processar sua solicitação.'
          description={error.msg}
          titleBold={false}
          titleFontSize={18}
          statusSlide={SlideStatusEnum.SLIDE_WARNING}
        />
      </Visibility>

      <Visibility visible={SHOW_MSG_SUCCESS}>
        <OpenSlideSuccess
          title='Sua solicitação para atualização de Documento Legal foi enviada com sucesso.'
          description={`O arquivo foi enviado com sucesso. Foi aberto o protocolo ${protocolNumber} para que o nosso time de atendimento faça a validação do documento.`}
          titleBold={false}
          titleFontSize={18}
        />
      </Visibility>

      <Visibility visible={SHOW_FORM}>
        <Form
          className="flex-1"
          id="send_legal_document_form"
          onValidate={() => onSubmitDocument()}
        >
          <Column className="w-100 max-w-lg-100 flex-1 px-0 mt-4">
            <Row className="flex-md-column mb-3">
              <Column className="flex-1 ml-sm-0 mt-md-2">
                <Text className="color-blue-gray mb-2">Tipo do documento*</Text>
                <Select
                  id='type_document'
                  name="Tipo do documento"
                  value={typeDocument.valueName}
                  onChange={(e) => handleAddTypeDocument(e)}
                  className="w-lg-100"
                  options={typeDocuments}
                  disabled={loading}
                  data-testid="document-type"
                />
              </Column>
              <Column className="flex-2 ml-4 ml-sm-0 mt-md-2">
                <Text className="color-blue-gray mb-2">Nome do documento*</Text>
                <InputNameDocument
                  value={nameDocument}
                  onChange={({ target }: ChangeEvent<HTMLInputElement>) =>
                    setNameDocument(target.value)
                  }
                  onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => makeRequestOnEnterPress(e)}
                  disabled={loading || typeDocument.valueName !== EnumTypeDocument.OUTROS}
                  maxLength={39}
                  data-testid="document-name"
                />
              </Column>
            </Row>

            <Text className="color-blue-gray mb-2">Faça o upload do arquivo*</Text>
            <FileDropZone data={dataFile} setData={setDataFile} accept={FileTypesAccept} />

            {errorMessage && (
              <Text size={14} className="color-danger animated fadeIn mt-3">
                {errorMessage}
              </Text>
            )}

            <Row className="mt-5 align-center justify-center">
              <Button
                disabled={loading}
                className="outline bg-primary flex-sm-1 align-center justify-center d-flex"
                onClick={handleValidateInputAndAddNewDocument}
              >
                <Text className="color-primary font-bold px-2">Adicionar Documentos</Text>
              </Button>
            </Row>

            <Text size={14} className="animated fadeIn mt-8">
              Lista de documentos
            </Text>

            <Visibility visible={newDocuments.length > 0}>
              <TableNewDocument
                trackingKey="Tabela Adicionar Documentos Legais"
                data={newDocuments}
                model={legalDocumentModelCompleteButton(removeLegalDocument)}
              />

              <Row className="mt-5 align-center justify-center">
                <Button
                  id="btn_legal_document"
                  form="send_legal_document_form"
                  spinnerColor="#0E4C54"
                  isRequesting={loading}
                  disabled={loading}
                  className="outline bg-primary flex-sm-1 align-center justify-center d-flex"
                >
                  <Text className="color-primary font-bold px-2">Enviar para Aprovação</Text>
                </Button>
              </Row>
            </Visibility>

            <Visibility visible={newDocuments.length === 0}>

              <TableNewDocument
                trackingKey="Tabela Adicionar Documentos Legais"
                data={newDocuments}
                model={legalDocumentModelCompleteButton(removeLegalDocument)}
              />

            </Visibility>
          </Column>
        </Form>
      </Visibility>
    </>
  );
};